import React from "react";
import { IconButton, Tooltip } from "@chakra-ui/react";
import { FiMoon, FiSun } from "react-icons/fi";
import { useAdminTheme } from "../../context/AdminThemeContext";
import { brandPalette } from "../../constants/brandPalette";

/**
 * Sun/moon toggle for the admin dark-mode preference.
 *
 * Two visual variants:
 *  - `tone="onMaroon"` (default): white-on-maroon, designed for the maroon
 *    sidebar footer where it sits next to the notifications bell and settings
 *    popover. Same in both light and dark modes.
 *  - `tone="onSurface"`: maroon-on-cream (light) / cream-on-dark-surface (dark),
 *    designed for the mobile top bar which sits on a cream/dark-surface
 *    background.
 */
const ThemeToggleButton = ({ size = "md", iconSize = 20, tone = "onMaroon" }) => {
  const { themeMode, toggleTheme } = useAdminTheme();
  const isDark = themeMode === "dark";

  const toneProps =
    tone === "onSurface"
      ? {
          color: "var(--text-primary)",
          _hover: { bg: "rgba(91, 13, 22, 0.08)" },
          _active: { bg: "rgba(91, 13, 22, 0.16)" },
          border: "2px solid",
          borderColor: brandPalette.primary,
          bg: "var(--surface-raised)",
          borderRadius: "full",
        }
      : {
          color: "white",
          _hover: { bg: "rgba(255,255,255,0.12)" },
          _active: { bg: "rgba(255,255,255,0.18)" },
          borderRadius: "12px",
        };

  return (
    <Tooltip
      label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      placement="top"
      hasArrow
    >
      <IconButton
        aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
        icon={isDark ? <FiSun size={iconSize} /> : <FiMoon size={iconSize} />}
        onClick={toggleTheme}
        variant="ghost"
        size={size}
        flexShrink={0}
        {...toneProps}
      />
    </Tooltip>
  );
};

export default ThemeToggleButton;
